import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CELLULAR_PROD_CODES } from '@bumble/app/constants';

interface RouterFeatureEditProps {
  onSubmit: () => void;
  cpeId: string;
  prodCode: string;
}

// The entitlement for the cellular API (APN, data pools); it doesn't control the cellular link.
const CELLULAR_FEATURE_KEY = 'bn_service_cellular';

const formatFeatureLabel = (featureKey: string) =>
  featureKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const RouterFeatureEdit = ({ onSubmit, cpeId, prodCode }: RouterFeatureEditProps) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [featureValues, setFeatureValues] = useState<Record<string, boolean>>({});

  const isCellularCapable = CELLULAR_PROD_CODES.includes(prodCode);

  const { isLoading, data } = useQuery(
    ['listFeatureEntitlements', cpeId],
    api.listFeatureEntitlementsApiV1ListFeatureEntitlementsGet.bind(null, {
      scope_type: 'cpe',
      scope_id: cpeId,
    }),
    { enabled: true, cacheTime: 0 },
  );

  const features = data?.data ?? [];

  useEffect(() => {
    const values: Record<string, boolean> = {};
    features.forEach((feature) => {
      values[feature.feature_key] = feature.enabled ?? false;
    });
    setFeatureValues(values);
  }, [data]);

  const setFeatureEntitlements = useMutation(api.setFeatureEntitlementsApiV1SetFeatureEntitlementsPost, {
    onSuccess() {
      toast.success('Feature entitlements updated successfully.');
      queryClient.invalidateQueries(['getCpeFeatureMatrix', cpeId]);
      onSubmit();
    },
    onError: apiError,
  });

  const isFeatureDisabled = (featureKey: string) =>
    featureKey.toLowerCase() === CELLULAR_FEATURE_KEY && !isCellularCapable;

  const handleToggle = (featureKey: string, checked: boolean) => {
    setFeatureValues((prev) => ({ ...prev, [featureKey]: checked }));
  };

  const handleSubmit = () => {
    setFeatureEntitlements.mutate({
      scope_type: 'cpe',
      scope_id: cpeId,
      entitlements: features.map((feature) => ({
        feature_key: feature.feature_key,
        enabled: featureValues[feature.feature_key] ?? false,
      })),
    });
  };

  if (isLoading) return <PageLoading content="Loading features..." />;

  return (
    <Box pt={2}>
      {setFeatureEntitlements.isLoading && <PageLoading content="Saving..." />}
      {features.length === 0 ? (
        <Typography variant="body2">No features available for this device.</Typography>
      ) : (
        features.map((feature) => (
          <FormControlLabel
            key={feature.feature_key}
            sx={{ display: 'flex', mb: 1 }}
            control={
              <Checkbox
                checked={featureValues[feature.feature_key] ?? false}
                disabled={isFeatureDisabled(feature.feature_key)}
                onChange={(e) => handleToggle(feature.feature_key, e.target.checked)}
              />
            }
            label={feature.name ?? formatFeatureLabel(feature.feature_key)}
          />
        ))
      )}
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button
          variant="outlined"
          onClick={handleSubmit}
          disabled={features.length === 0 || setFeatureEntitlements.isLoading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
